import Button from 'components/_UI/Button';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import QuillResizeImage from 'quill-resize-image';


const Size = Quill.import('attributors/style/size');
Size.whitelist = ['18px', '16px', '12px', '10px'];
Quill.register(Size, true);
Quill.register("modules/resize", QuillResizeImage);

const MAX_DESCRIPTION_IMAGES = 3;
const MAX_IMAGES_MESSAGE = `Você atingiu o limite de ${MAX_DESCRIPTION_IMAGES} imagens na descrição.`;

function base64ToFile(base64, fileName) {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const b64Data = arr[1];
  const byteCharacters = atob(b64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mime });

  return new File([blob], fileName, { type: mime });
}

const RichTextEditor = forwardRef(function RichTextEditor({ edit, onChangePureText, onChange, onToggleImage = () => {}, descriptionImageUploads = [], setErrorTypeImageDescription = () => {}, errorTypeImageDescription = '', allowImages = true, placeholder }, ref) {
  const [content, setContent] = useState('');
  const [selectedSize, setSelectedSize] = useState('Normal');
  const [showPreview, setShowPreview] = useState(false);

  const quillRef = useRef(null);

  // Permite ao componente pai inserir texto no cursor atual (ex: variáveis
  // de personalização), sem expor o editor Quill inteiro pra fora.
  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      quill.insertText(index, text, 'user');
      quill.setSelection(index + text.length);
    }
  }));

  // Chave de identificação de cada imagem em voo: o próprio base64 (fonte já
  // única por conteúdo), não um atributo customizado no DOM. Um atributo como
  // `data-upload-id` parece a solução óbvia, mas o react-quill é um componente
  // controlado: a cada mudança ele reconverte `value` (HTML) de volta pra um
  // Delta via `editor.clipboard.convert`, e esse conversor só entende os
  // atributos registrados no formato de imagem do Quill (basicamente `src`) —
  // qualquer atributo extra que a gente grave direto no DOM é descartado nessa
  // reconversão. Isso reabre `hasBase64Image`/"ainda não processada" a cada
  // ciclo e gera um loop infinito (React derruba com "Maximum update depth
  // exceeded"). Usar o próprio `src` como chave evita mexer no DOM além do que
  // o Quill já reconhece, então nada se perde na reconversão e o ciclo fecha.
  const dispatchedSrcs = useRef(new Set());
  const appliedSrcs = useRef(new Set());

  // O módulo `image` do toolbar é montado uma única vez (via useMemo) para não
  // forçar o react-quill a recriar o editor inteiro a cada re-render do form
  // pai — por isso os callbacks/estado usados dentro dele vêm de refs "ao vivo",
  // atualizadas a cada render, em vez de fechar sobre as props diretamente.
  const onToggleImageRef = useRef(onToggleImage);
  const setErrorTypeImageDescriptionRef = useRef(setErrorTypeImageDescription);
  const errorTypeImageDescriptionRef = useRef(errorTypeImageDescription);

  useEffect(() => {
    onToggleImageRef.current = onToggleImage;
    setErrorTypeImageDescriptionRef.current = setErrorTypeImageDescription;
    errorTypeImageDescriptionRef.current = errorTypeImageDescription;
  });

  function imageHandler() {
    const currentCount = this.quill.root.querySelectorAll('img').length;

    if (currentCount >= MAX_DESCRIPTION_IMAGES) {
      setErrorTypeImageDescriptionRef.current(MAX_IMAGES_MESSAGE);
      return;
    }

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const range = this.quill.getSelection(true);
        this.quill.insertEmbed(range.index, 'image', reader.result, 'user');
        this.quill.setSelection(range.index + 1);
      };
      reader.readAsDataURL(file);
    };
  }

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ size: ['18px', '16px', '12px', '10px'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ align: [] }],
        [{ color: Object.values(colors) }, { background: Object.values(colors) }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ...(allowImages ? [['image']] : []),
        ['link']
      ],
      handlers: allowImages ? { image: imageHandler } : {}
    },
    resize: {
      locale: {
        floatLeft: 'Esquerda',
        center: 'Centro',
        floatRight: 'Direita',
        restore: 'Resetar',
        altTip: 'Segure Alt para travar a proporção'
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [allowImages]);

  const formats = [
    'size',
    'bold', 'italic', 'underline', 'strike',
    'align',
    'color', 'background',
    'list', 'bullet',
    'image',
    'link',
  ]

  // Aplica, na(s) imagem(ns) certa(s) (por src, não por posição), o link real
  // assim que o upload é resolvido — mesmo quando há mais de uma em voo.
  useEffect(() => {
    if (!quillRef.current) return;

    const root = quillRef.current.getEditor().root;
    let didApply = false;

    descriptionImageUploads.forEach(({ uploadId: base64Src, path }) => {
      if (!path || appliedSrcs.current.has(base64Src)) return;

      const matches = Array.from(root.querySelectorAll('img'))
        .filter((img) => img.getAttribute('src') === base64Src);

      if (!matches.length) return;

      matches.forEach((img) => img.setAttribute('src', path));
      appliedSrcs.current.add(base64Src);
      didApply = true;
    });

    if (didApply) {
      handleChange(root.innerHTML);
    }
  }, [descriptionImageUploads]);

  // Listener do rótulo do seletor de tamanho (título/subtítulo/normal/pequeno):
  // monta uma vez e limpa no unmount, para não empilhar listeners duplicados.
  // Usa a barra deste editor: com mais de um editor na página (ingressos com
  // recado), buscar no documento inteiro pegava sempre a primeira barra.
  useEffect(() => {
    const toolbar = quillRef.current?.getEditor().getModule('toolbar')?.container;
    const quillSizePicker = toolbar?.querySelector('.ql-size');
    if (!quillSizePicker) return;

    function handleSizeClick() {
      const selectedItem = toolbar.querySelector('.ql-size .ql-selected');
      if (!selectedItem) return;

      const size = selectedItem.getAttribute('data-value');
      switch (size) {
        case '18px':
          setSelectedSize('Título');
          break;
        case '16px':
          setSelectedSize('Subtítulo');
          break;
        case '12px':
          setSelectedSize('Normal');
          break;
        case '10px':
          setSelectedSize('Pequeno');
          break;
        default:
          setSelectedSize('Normal');
      }
    }

    quillSizePicker.addEventListener('click', handleSizeClick);
    return () => quillSizePicker.removeEventListener('click', handleSizeClick);
  }, []);

  // Carrega o conteúdo existente uma única vez, ao montar (em edição).
  useEffect(() => {
    if (edit) {
      handleChange(edit?.description_with_html);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (value) => {

    if (value) {
      const hasBase64Image = value.includes('data:image');

      if (!hasBase64Image && errorTypeImageDescriptionRef.current) {
        setErrorTypeImageDescriptionRef.current('');
      }

      if (hasBase64Image && quillRef.current) {
        const root = quillRef.current.getEditor().root;
        const images = Array.from(root.querySelectorAll('img'));
        let totalImages = images.length;
        let changedDom = false;

        images.forEach((img) => {
          const src = img.getAttribute('src') || '';
          const isBase64 = src.startsWith('data:image');

          if (!isBase64 || dispatchedSrcs.current.has(src)) return;

          if (totalImages > MAX_DESCRIPTION_IMAGES) {
            img.remove();
            totalImages -= 1;
            changedDom = true;
            setErrorTypeImageDescriptionRef.current(MAX_IMAGES_MESSAGE);
            return;
          }

          dispatchedSrcs.current.add(src);
          const file = base64ToFile(src, `image_description_${dispatchedSrcs.current.size}`);
          onToggleImageRef.current(file, src);
        });

        if (changedDom) {
          value = root.innerHTML;
        }
      }
    }

    setContent(value);

    onChange(value);

    if (quillRef.current) {
      const quillEditor = quillRef.current.getEditor();
      const pureText = quillEditor.getText();
      onChangePureText(pureText);
      return
    }

    onChangePureText('');
  };



  return (
    <Container selectedSize={selectedSize}>
      <ReactQuill
        ref={quillRef}
        value={content}
        onChange={handleChange}
        theme="snow"
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />

      {
        !!content?.length && (
          <Button
            marginTop={'18px'}
            category='outline'
            onClick={() => setShowPreview(true)}
          >
            Pré visualizar conteudo
          </Button>

        )
      }

      {
        showPreview && (
          <Modal
            size='large'
            isOpen={showPreview}
            closeButton={true}
            onClose={() => setShowPreview(false)}
          >
            <Column>
              <Text name='subtitle'>Pré visualização</Text>
              <PreviewContent dangerouslySetInnerHTML={{ __html: content }} />
            </Column>
          </Modal>
        )

      }
    </Container>
  );
});

export default RichTextEditor;

// O modal de preview é renderizado via portal direto em document.body (fora
// da árvore DOM de Container), então o CSS de imagem precisa de um wrapper
// próprio — regras dentro de Container nunca chegariam até ele.
const PreviewContent = styled.div`
  img {
    max-width: 100%;
    height: auto;
  }
`;

const Container = styled.div`
  margin-bottom: 18px;

  .ql-editor img {
    max-width: 100%;
    height: auto;
  }

  .ql-editor {
    max-height: 400px;
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label::before {
    content: '${props => props.selectedSize}';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
    content: 'Título';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
    content: 'Subtítulo';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
    content: 'Normal';
  }

  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
    content: 'Pequeno';
  }

  .ql-snow.ql-toolbar button {
    width: auto;
  }

  .ql-size-large {
    font-size: ${fonts.sizes.highlight};
  }

  .ql-size-subtitle {
    font-size: ${fonts.sizes.subtitle};
  }

  .ql-size-normal {
    font-size: ${fonts.sizes.default};
  }

  .ql-size-small {
    font-size: ${fonts.sizes.small};
  }

  .ql-toolbar.ql-snow {
    padding: 0 8px;
    text-align: left;
  }

  .ql-toolbar.ql-snow .ql-formats {
    border-right: 1px solid ${colors.nightLight};
    padding: 8px 10px;
    margin-right: 0;
  }

  .ql-snow .ql-picker.ql-size .ql-picker-label {
    width: auto;
    padding-right: 25px;
  }

  .ql-snow .ql-tooltip.ql-editing a.ql-action::after {
    content: 'Salvar';
  }

  .ql-snow .ql-tooltip a.ql-action::after {
    content: 'Editar';
  }

  .ql-snow .ql-tooltip::before {
    content: 'link:';
  }

  .ql-snow .ql-tooltip a.ql-remove::before {
    content: 'Remover';
  }

  /* A caixa de seleção de resize do plugin vem com borda tracejada branca
     (invisível no fundo claro do editor) e uma alça de ~10px — deixamos as
     duas mais visíveis e mais fáceis de "pegar" com o mouse. */
  #editor-resizer {
    border: 1px dashed rgba(0, 0, 0, .35) !important;
  }

  #editor-resizer .handler {
    width: 14px !important;
    height: 14px !important;
    border: 1px solid rgba(0, 0, 0, .45) !important;
    box-shadow: 0 0 2px rgba(0, 0, 0, .3);
  }
`;
